import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataItem, updateDailySubContractor } from '../../../../services/apiSLTP/activities';
import { createDailySubContractor, DailySubContractor, getSubContractorTypes } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getPriceTypeInfos, priceTypeEnum, stepModes, systemConfigKeyEnum } from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import DropDown, { OptionType } from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ManageDailySubContractorModalProps extends ModalProps {
    activity: DataItem;
    activityIndex: number;
    refetch: () => void;
    dailySubContractor: DailySubContractor | null;
}

const ManageDailySubContractorModal: React.FC<ManageDailySubContractorModalProps> = ({
    open,
    handleClose,
    activity,
    refetch,
    dailySubContractor,
    
}) => {
    const { t } = useTranslation();
    const {getConfigValue} = useSystemConfig();
    const [subContractorsTypes, setSubContractorsTypes] = useState<{ label: string; value: string }[]>([]);
    const [selectedSubContractorType, setSelectedSubContractorType] = useState({ label: '', value: '' });
    const [currentSubContractorId, setCurrentSubContractorId] = useState(dailySubContractor?._id);
    const [price, setPrice] = useState(typeof dailySubContractor?.price === 'number' ? dailySubContractor?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailySubContractor?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeInfos(dailySubContractor?.priceType))),
                  value: (dailySubContractor?.priceType).toString(),
              }
            : null
    );

    const fetchSubContractorsTypes = async () => {
        const response = await getSubContractorTypes();
        setSubContractorsTypes(
            response.data.map((value) => {
                return { label: value.designation, value: value._id };
            })
        );
    };

    const postDailySubContractor = async () => {
        const response = await createDailySubContractor({
            activityId: activity._id,
            subContractorTypeId: selectedSubContractorType.value,
            price: price ? parseFloat(price) : undefined,
            priceType: priceType?.value ? parseInt(priceType.value) : undefined,
        });
        setCurrentSubContractorId(response.data._id);
        refetch();
    };

    const patchDailySubContractor = async () => {
        if (!currentSubContractorId) return;
        const response = await updateDailySubContractor(currentSubContractorId, {
            subContractorTypeId: selectedSubContractorType.value,
            price: price ? parseFloat(price) : null,
            priceType: priceType?.value ? parseInt(priceType.value) : null,
        });
        refetch();
    };

    useEffect(() => {
        fetchSubContractorsTypes();
        if (dailySubContractor) {
            setSelectedSubContractorType({
                label: dailySubContractor.subContractorTypeId?.designation || '',
                value: dailySubContractor.subContractorTypeId?._id || '',
            });
        }
    }, []);

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('subContractor')}
            subTitle={!!currentSubContractorId?.length ? t('update') : t('add')}
            description={activity.siteId.name}
            okText={t('valider')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentSubContractorId?.length ? postDailySubContractor() : patchDailySubContractor();
            }}
            disabledOk={!selectedSubContractorType.value.length}
            
        >
            <DropDown
                selectedItem={selectedSubContractorType}
                items={subContractorsTypes}
                handleChange={(event, value) => setSelectedSubContractorType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('subContractor')}
                required
            />
            {activity.stepMode === stepModes.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailySubContractorModal;
